import { memo } from 'react';
import { useAsync } from 'react-use';
import { ColorUtils, ObjectUtils } from '@zmedia/bm-soccer-ui/utils';
import MatchService from '@zmedia/bm-soccer-ui/service/MatchService';
import {
    Element,
    Image,
    Section,
    Title,
} from '@zmedia/bm-soccer-ui/components';

import styles from './style.module.scss';

interface IPredict {
    result: string;
    percent: number;
    opacity: number;
}

export function groupByResult(items: any[]) {
    let group: any = [];
    items.forEach((item) => {
        let key: string = Math.abs(
            item.result.split('-')[0] - item.result.split('-')[1]
        ).toString();
        if (!group[key]) {
            group[key] = [];
        }
        group[key].push(item);
    });
    return group;
}

const DRAW_COLOR = '#717171';

export function PredictDetail(props: any) {
    const { id, teamColor, home, away } = props;

    let data = props.predict || {};
    if (ObjectUtils.isEmpty(data)) {
        const state = useAsync(() => MatchService.getPredict(id));
        data = state.value?.data || {};
    }

    const { result = {}, probability = {} } = data;

    if (ObjectUtils.isEmpty(result)) {
        return null;
    }

    const { hwin, draw, awin } = result;
    const {
        hwin: percentHwin,
        draw: percentDraw,
        awin: percentAwin,
    } = probability;

    draw.sort((a: IPredict, b: IPredict) => {
        if (a.percent > b.percent) {
            return -1;
        }
        if (a.percent < b.percent) {
            return 1;
        }
        return 0;
    });

    const renderItem = (item: IPredict, color: string, index: number) => {
        return (
            <Element className={styles['predict-item']} key={index}>
                <Element
                    className={styles['predict-info']}
                    style={{
                        backgroundColor: `rgba(
                            ${ColorUtils.convertToRGB(color)},
                            ${item.opacity}
                        )`,
                        color:
                            !ColorUtils.isBrightnessColor(color) &&
                            item.opacity > 0.5
                                ? '#fff'
                                : '',
                    }}
                >
                    <Element>{item.result}</Element>
                    <Element>{item.percent}%</Element>
                </Element>
            </Element>
        );
    };

    const renderList = (group: IPredict[] = [], color: string) => {
        return group.map((item: IPredict, index: number) => {
            return renderItem(item, color, index);
        });
    };

    return (
        <Section className={styles['predict-section']}>
            <Title renderAs="h2">Dự đoán máy tính</Title>
            <Element className={styles['predict-winner']}>
                <Element className={styles['predict-teams']}>
                    <Image src={home.team.logo} alt={home.team.name} />
                    <Image src={away.team.logo} alt={away.team.name} />
                </Element>
                <Element className={styles['elo-bar']}>
                    <Element
                        className={styles['home-bar']}
                        style={{ width: percentHwin + '%' }}
                    ></Element>
                    <Element
                        className={styles['draw-bar']}
                        style={{ width: percentDraw + '%' }}
                    ></Element>
                    <Element
                        className={styles['away-bar']}
                        style={{ width: percentAwin + '%' }}
                    ></Element>
                </Element>
                <Element className={styles['elo-info']}>
                    <Element className={styles['home-info']}>
                        <Element className={styles['text']}>
                            Thắng
                            <Element
                                className={styles['percent']}
                                style={{
                                    color: ColorUtils.isBrightnessColor(
                                        teamColor?.hColor
                                    )
                                        ? '#000'
                                        : '',
                                }}
                            >
                                {percentHwin}%
                            </Element>
                        </Element>
                    </Element>
                    <Element className={styles['draw-info']}>
                        <Element className={styles['text']}>
                            Hòa
                            <Element className={styles['percent']}>
                                {percentDraw}%
                            </Element>
                        </Element>
                    </Element>
                    <Element className={styles['away-info']}>
                        <Element className={styles['text']}>
                            Thắng
                            <Element
                                className={styles['percent']}
                                style={{
                                    color: ColorUtils.isBrightnessColor(
                                        teamColor?.aColor
                                    )
                                        ? '#000'
                                        : '',
                                }}
                            >
                                {percentAwin}%
                            </Element>
                        </Element>
                    </Element>
                </Element>
            </Element>
            <Element className={styles['predict-scores']}>
                <Element className={styles['text-score']}>
                    {home.team.name} thắng
                </Element>
                <Element>
                    {groupByResult(hwin)
                        ?.reverse()
                        ?.map((group: IPredict[], index: number) => {
                            return (
                                <Element
                                    key={index}
                                    className={styles['predict-list']}
                                >
                                    {renderList(group, teamColor.hColor)}
                                </Element>
                            );
                        })}
                </Element>
            </Element>

            <Element className={styles['predict-scores']}>
                <Element className={styles['text-score']}>Hòa</Element>
                <Element className={styles['predict-list']}>
                    {renderList(draw, DRAW_COLOR)}
                </Element>
            </Element>

            <Element className={styles['predict-scores']}>
                <Element className={styles['text-score']}>
                    {away.team.name} thắng
                </Element>
                <Element>
                    {groupByResult(awin)?.map(
                        (group: IPredict[], index: number) => {
                            return (
                                <Element
                                    key={index}
                                    className={styles['predict-list']}
                                >
                                    {renderList(group, teamColor.aColor)}
                                </Element>
                            );
                        }
                    )}
                </Element>
            </Element>
        </Section>
    );
}

export default memo(PredictDetail);
